import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import { login, logout } from "services/authService"
import { getUserProfile } from "services/userService"
import { clearTokens, issueToken, parseToken } from "utils/tokenManager"

const TENANT_STORAGE_KEY = "tenantId"
const getTenantIdFromAuthPayload = authData =>
  authData?.tenantId ||
  authData?.tenant?._id ||
  authData?.tenant?.id ||
  authData?.user?.tenantId ||
  authData?.profile?.tenantId ||
  (authData?.token ? parseToken(authData.token)?.tenantId : null) ||
  null

// ── Async thunks ──────────────────────────────────────────────────────────────

export const loginUser = createAsyncThunk(
  "auth/loginUser",
  async ({ email, password, history }, { rejectWithValue }) => {
    try {
      const data = await login(email, password)
      if (!data?.status) return rejectWithValue(data?.msg || "Login failed")
      if (history) history("/dashboard")
      return data.data
    } catch (err) {
      return rejectWithValue(err?.message || "Login failed")
    }
  }
)

export const logoutUser = createAsyncThunk(
  "auth/logoutUser",
  async (_, { rejectWithValue }) => {
    try {
      await logout()
    } catch (err) {
      return rejectWithValue(err?.message || "Logout failed")
    }
  }
)

export const refreshToken = createAsyncThunk(
  "auth/refreshToken",
  async (_, { rejectWithValue }) => {
    try {
      const storedRefresh = localStorage.getItem("refreshToken")
      if (!storedRefresh) return rejectWithValue("No refresh token available")
      const { default: api } = await import("services/api")
      const response = await api.post("auth/refresh-token", {
        refreshToken: storedRefresh,
      })
      const data = response.data
      const tokenPayload = data?.data?.token ? data.data : data
      if (data?.status && tokenPayload?.token) {
        issueToken(tokenPayload)
        return tokenPayload
      }
      return rejectWithValue(data?.msg || "Token refresh failed")
    } catch (err) {
      return rejectWithValue(err?.message || "Token refresh failed")
    }
  }
)

export const fetchUserProfile = createAsyncThunk(
  "auth/fetchUserProfile",
  async (_, { rejectWithValue }) => {
    try {
      const data = await getUserProfile()
      if (!data?.status) return rejectWithValue(data?.msg || "Profile fetch failed")
      return data.data
    } catch (err) {
      return rejectWithValue(err?.message || "Profile fetch failed")
    }
  }
)

// ── Slice ─────────────────────────────────────────────────────────────────────

const initialState = {
  user: null,
  isAuthenticated: false,
  loading: false,
  error: null,
  tokens: {
    accessToken: null,
    refreshToken: null,
  },
}

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    clearAuthError(state) {
      state.error = null
    },
    resetAuth(state) {
      state.user = null
      state.isAuthenticated = false
      state.error = null
      state.tokens = { accessToken: null, refreshToken: null }
      clearTokens()
    },
  },
  extraReducers: builder => {
    // loginUser
    builder
      .addCase(loginUser.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false
        state.user = action.payload
        const tenantId = getTenantIdFromAuthPayload(action.payload)
        console.log("tenantId", tenantId)
        if (tenantId) {
          localStorage.setItem(TENANT_STORAGE_KEY, tenantId)
        }
        state.isAuthenticated = true
        state.tokens.accessToken = action.payload?.token || null
        state.tokens.refreshToken = action.payload?.refreshToken || null
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.isAuthenticated = false
      })

    // logoutUser
    builder
      .addCase(logoutUser.pending, state => {
        state.loading = true
      })
      .addCase(logoutUser.fulfilled, state => {
        state.loading = false
        state.user = null
        state.isAuthenticated = false
        state.tokens = { accessToken: null, refreshToken: null }
      })
      .addCase(logoutUser.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        // Still clear local state on logout failure
        state.user = null
        state.isAuthenticated = false
        state.tokens = { accessToken: null, refreshToken: null }
      })

    // refreshToken
    builder
      .addCase(refreshToken.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(refreshToken.fulfilled, (state, action) => {
        state.loading = false
        const tenantId = getTenantIdFromAuthPayload(action.payload)
        console.log("tenantId", tenantId)
        if (tenantId) {
          localStorage.setItem(TENANT_STORAGE_KEY, tenantId)
        }
        state.tokens.accessToken = action.payload?.token || null
        if (action.payload?.refreshToken) {
          state.tokens.refreshToken = action.payload.refreshToken
        }
      })
      .addCase(refreshToken.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

    // fetchUserProfile
    builder
      .addCase(fetchUserProfile.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchUserProfile.fulfilled, (state, action) => {
        state.loading = false
        state.user = action.payload
      })
      .addCase(fetchUserProfile.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
  },
})

export const { clearAuthError, resetAuth } = authSlice.actions

// ── Selectors ─────────────────────────────────────────────────────────────────

export const selectUser = state => state.Auth?.user
export const selectIsAuthenticated = state => state.Auth?.isAuthenticated ?? false
export const selectAuthLoading = state => state.Auth?.loading ?? false
export const selectAuthError = state => state.Auth?.error

export default authSlice.reducer
